import { useTranslation } from "react-i18next";

import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { useSpVerifyAccess } from "~/api/v2/useSp";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "../NoResult";
import { updateOnboardingState } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { Check, X } from "lucide-react";

type Props = {
  onSubmit: () => void;
};

export const VerifyAccessStep = ({ onSubmit }: Props) => {
  const { t } = useTranslation();

  const { data, isLoading } = useSpVerifyAccess();

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!data) {
    return <NoResult />;
  }

  const handleSubmit = async () => {
    try {
      await updateOnboardingState({ onboardingCompleted: true });
      onSubmit();
    } catch (err) {
      console.error(err);
      toast.error(t(($) => $.sharepoint.steps.verifyAccess.error));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <span>{t(($) => $.sharepoint.steps.verifyAccess.header)}</span>
        </CardTitle>
        <CardDescription>
          {t(($) => $.sharepoint.steps.verifyAccess.subtitle)}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.ok ? (
          <div className="py-8 text-center">
            <Check className="mx-auto mb-4 h-16 w-16 text-green-500" />
            <h3 className="mb-2 text-xl font-semibold">
              {t(($) => $.sharepoint.steps.verifyAccess.success)}
            </h3>
          </div>
        ) : (
          <div className="py-8 text-center">
            <X className="mx-auto mb-4 h-16 w-16 text-red-500" />
            <h3 className="mb-2 text-xl font-semibold">
              {t(($) => $.sharepoint.steps.verifyAccess.error)}
            </h3>
            <p className="text-muted-foreground">
              {t(($) => $.sharepoint.steps.verifyAccess.errorDescription)}
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {t(($) => $.commons.buttons.finish)}
        </Button>
      </CardFooter>
    </Card>
  );
};
